import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { CustomFieldAnswer } from "@nova/schemas"
import { useSWRNoRevalidate } from "@nova/swr"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useAppSelector } from "integrations/redux/hooks"
import { mutate } from "swr"
import useSWRMutation from "swr/mutation"

export const invalidateMissionCustomFields = (missionId?: string) =>
    mutate(
        (key) =>
            Array.isArray(key) &&
            key[0] === "get-mission-custom-fields" &&
            (missionId === undefined || key[2] === missionId)
    )

const getMissionCustomFields = ({ orgId, missionId }: { orgId: string; missionId: string }) => {
    return fetchApiGateway("get /org/:orgId/fleet/mission/:missionId/custom-fields", {
        params: { orgId, missionId },
    })
}

export const useMissionCustomFields = ({ missionId }: { missionId: string }) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { data, isLoading } = useSWRNoRevalidate(
        ["get-mission-custom-fields", orgId, missionId],
        () => (orgId ? getMissionCustomFields({ orgId, missionId }) : undefined)
    )

    return { data, isLoading }
}

const upsertMissionCustomFields = ({
    orgId,
    missionId,
    answers,
}: {
    orgId: string
    missionId: string
    answers: CustomFieldAnswer[]
}) => {
    return fetchApiGateway("put /org/:orgId/fleet/mission/:missionId/custom-fields", {
        body: { answers },
        params: { orgId, missionId },
    })
}

export const useUpsertMissionCustomFields = ({ missionId }: { missionId: string }) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        ["upsert-mission-custom-fields", orgId, missionId],
        (key, { arg }: { arg: { answers: CustomFieldAnswer[] } }) =>
            orgId
                ? upsertMissionCustomFields({ orgId, missionId, answers: arg.answers })
                : undefined,
        { onSuccess: () => invalidateMissionCustomFields(missionId) }
    )

    return { upsertMissionCustomFields: trigger, isMutating }
}

const upsertMissionCustomFieldAnswer = ({
    orgId,
    missionId,
    questionId,
    value,
}: {
    orgId: string
    missionId: string
    questionId: string
    value: CustomFieldAnswer["value"]
}) => {
    return fetchApiGateway("put /org/:orgId/fleet/mission/:missionId/custom-fields/:questionId", {
        body: { value },
        params: { orgId, missionId, questionId },
    })
}

export const useUpsertMissionCustomFieldAnswer = ({ missionId }: { missionId: string }) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        ["upsert-mission-custom-field-answer", orgId, missionId],
        (key, { arg }: { arg: { questionId: string; value: CustomFieldAnswer["value"] } }) =>
            orgId
                ? upsertMissionCustomFieldAnswer({
                      orgId,
                      missionId,
                      questionId: arg.questionId,
                      value: arg.value,
                  })
                : undefined,
        { onSuccess: () => invalidateMissionCustomFields(missionId) }
    )

    return { upsertMissionCustomFieldAnswer: trigger, isMutating }
}
